import { useEffect, useId } from "react";
import * as Checkbox from "@radix-ui/react-checkbox";
import type { Question } from "../../lib/survey-config";
import type { MultiSelectValue, QuestionProps } from "./types";
import { QuestionHeader } from "./QuestionHeader";

type MultiSelectQuestionT = Extract<Question, { type: "multiSelect" }>;

function isValid(q: MultiSelectQuestionT, v: MultiSelectValue): boolean {
  const total = v.selected.length + (v.other.trim().length > 0 ? 1 : 0);
  const min = q.minSelections ?? (q.required !== false ? 1 : 0);
  return total >= min;
}

export function MultiSelectQuestion({
  question,
  value,
  onChange,
  onValid,
  autoFocus,
}: QuestionProps<MultiSelectQuestionT, MultiSelectValue>) {
  const baseId = useId();

  useEffect(() => {
    onValid(isValid(question, value));
  }, [question, value, onValid]);

  function toggle(optValue: string, checked: boolean) {
    const next = checked
      ? [...value.selected.filter((v) => v !== optValue), optValue]
      : value.selected.filter((v) => v !== optValue);
    onChange({ ...value, selected: next });
  }

  return (
    <div className="space-y-4">
      <QuestionHeader
        title={question.title}
        subtitle={question.subtitle}
        required={question.required !== false}
      />
      <div className="space-y-2">
        {question.options.map((opt, idx) => {
          const id = `${baseId}-${opt.value}`;
          const checked = value.selected.includes(opt.value);
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className="flex cursor-pointer items-center gap-3 rounded-input border border-border bg-bg-elevated px-4 py-3 transition hover:border-accent has-[:checked]:border-accent has-[:checked]:bg-accent/5"
            >
              <Checkbox.Root
                id={id}
                checked={checked}
                onCheckedChange={(c) => toggle(opt.value, c === true)}
                autoFocus={autoFocus && idx === 0}
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 border-border bg-bg-elevated data-[state=checked]:border-accent data-[state=checked]:bg-accent"
              >
                <Checkbox.Indicator className="text-accent-fg">
                  <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </Checkbox.Indicator>
              </Checkbox.Root>
              <span className="text-sm leading-snug">{opt.label}</span>
            </label>
          );
        })}
        {question.allowOther ? (
          <label className="block space-y-2 rounded-input border border-dashed border-border bg-bg-elevated px-4 py-3">
            <span className="text-xs font-medium text-fg-muted">Sonstiges</span>
            <input
              type="text"
              value={value.other}
              onChange={(e) => onChange({ ...value, other: e.target.value })}
              placeholder="Etwas anderes …"
              className="w-full bg-transparent text-sm outline-none"
            />
          </label>
        ) : null}
      </div>
    </div>
  );
}
